import type { AdminBattleItem, AdminBattleResponse } from "../../../types/api.generated";

export type { AdminBattleItem, AdminBattleResponse };

export interface AdminBattleQueryParams {
  page?: number;
  limit?: number;
}

export interface AdminBattleCreatePayload {
  title: string;
  startTime: string;
  automaticDistributionDelay: number;
  participations: { characterId: string; isWinner: boolean }[];
}

export interface AdminBattleVideoUploadURLResponse {
  uploadURL: string;
  uid: string;
}

export interface AdminBattleCreateResponse {
  id: string;
}

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("tokenUser");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || "";

export const getAdminBattleCollection = async (params: AdminBattleQueryParams = {}): Promise<AdminBattleResponse> => {
  const { page, limit } = params;
  const base = getBaseUrl();
  const url = new URL(`${base}/admin/battles`, window.location.origin);
  if (page) url.searchParams.set("page", String(page));
  if (limit) url.searchParams.set("limit", String(limit));

  const response = await fetch(base ? `${base}/admin/battles${url.search}` : `/admin/battles${url.search}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch battles: ${response.status} ${response.statusText} ${text}`);
  }

  return response.json();
};

export const createAdminBattle = async (payload: AdminBattleCreatePayload): Promise<AdminBattleCreateResponse> => {
  const base = getBaseUrl();
  const response = await fetch(base ? `${base}/admin/battles` : `/admin/battles`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to create battle: ${response.status} ${response.statusText} ${text}`);
  }
  return response.json();
};

export const cancelAdminBattle = async (battleId: string | number): Promise<any> => {
  const base = getBaseUrl();
  const response = await fetch(
    base ? `${base}/admin/battles/${battleId}/cancel` : `/admin/battles/${battleId}/cancel`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to cancel battle: ${response.status} ${response.statusText} ${text}`);
  }
  return response.json();
};

export const getAdminBattleVideoUploadUrl = async (battleId: string | number): Promise<AdminBattleVideoUploadURLResponse> => {
  const base = getBaseUrl();
  const response = await fetch(
    base ? `${base}/admin/battles/${battleId}/video-upload-url` : `/admin/battles/${battleId}/video-upload-url`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to get video upload URL: ${response.status} ${response.statusText} ${text}`);
  }
  return response.json();
};
